<div class="dashboard-container">
  <!-- Header Section -->
  <div class="dashboard-header">
    <div class="header-content">
      <div class="title-section m-b-20">
        <h1 class="dashboard-title">Monthly Billing Summary</h1>
        <p class="dashboard-subtitle">Billable and non-billable counts for all companies</p>
      </div>
      
      <!-- Month Navigation -->
      <div class="date-navigation-container" 
           fxLayout="row" 
           fxLayoutAlign="center center" 
           fxLayoutGap="8px"
           style="max-width: 280px;">
        <button mat-icon-button 
                (click)="navigateToPreviousMonth()" 
                [disabled]="isPreviousMonthDisabled()"
                matTooltip="Previous Month"
                style="flex-shrink: 0; min-width: 40px;">
          <mat-icon>chevron_left</mat-icon>
        </button>
        
        <mat-form-field appearance="outline" 
                        class="date-selector"
                        style="flex: 1; min-width: 0;">
          <mat-label>Select Month</mat-label>
          <input matInput 
                 [matDatepicker]="monthPicker" 
                 [(ngModel)]="selectedMonth"
                 [min]="minDate"
                 [max]="maxDate"
                 readonly>
          <mat-datepicker-toggle matSuffix [for]="monthPicker"></mat-datepicker-toggle>
          <mat-datepicker #monthPicker 
                          startView="multi-year" 
                          (monthSelected)="onMonthChange($event); monthPicker.close()">
          </mat-datepicker>
        </mat-form-field>
        
        <button mat-icon-button 
                (click)="navigateToNextMonth()" 
                [disabled]="isNextMonthDisabled()"
                matTooltip="Next Month"
                style="flex-shrink: 0; min-width: 40px;">
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>
    </div>
  </div>

  <!-- Content Section -->
  <div class="dashboard-content" fxLayout="column" fxLayoutGap="16px">
    
    <!-- Key Metrics Row at Top -->
    <div class="metrics-sidebar" *ngIf="billingSummaries.length > 0">
      <div fxLayout="row wrap"
           fxLayout.lt-md="column"
           fxLayoutGap="16px"
           fxLayoutGap.lt-md="12px">

        <mat-card class="metric-card" fxFlex="1 1 calc(25% - 12px)" fxFlex.lt-md="1 1 calc(50% - 12px)" fxFlex.lt-sm="100">
          <mat-card-content fxLayout="column" fxLayoutAlign="center center">
            <div class="metric-value" [style.color]="'hsl(145, 60%, 55%)'">{{ getTotalBillable() | number }}</div>
            <div class="metric-label">Billable</div>
            <div class="metric-subtitle">Revenue generating</div>
          </mat-card-content>
        </mat-card>

        <mat-card class="metric-card" fxFlex="1 1 calc(25% - 12px)" fxFlex.lt-md="1 1 calc(50% - 12px)" fxFlex.lt-sm="100">
          <mat-card-content fxLayout="column" fxLayoutAlign="center center">
            <div class="metric-value" [style.color]="'hsl(330, 90%, 55%)'">{{ getTotalNonBillable() | number }}</div>
            <div class="metric-label">Non-Billable</div>
            <div class="metric-subtitle">Free usage</div>
          </mat-card-content>
        </mat-card>

        <mat-card class="metric-card" fxFlex="1 1 calc(25% - 12px)" fxFlex.lt-md="1 1 calc(50% - 12px)" fxFlex.lt-sm="100">
          <mat-card-content fxLayout="column" fxLayoutAlign="center center">
            <div class="metric-value" [style.color]="'hsl(210, 90%, 55%)'">{{ getGrandTotal() | number }}</div>
            <div class="metric-label">Total Impressions</div>
            <div class="metric-subtitle">{{ getMonthYearDisplay() }}</div>
          </mat-card-content>
        </mat-card>

        <mat-card class="metric-card" fxFlex="1 1 calc(25% - 12px)" fxFlex.lt-md="1 1 calc(50% - 12px)" fxFlex.lt-sm="100">
          <mat-card-content fxLayout="column" fxLayoutAlign="center center">
            <div class="metric-value" [style.color]="'hsl(35, 90%, 55%)'">{{ getTotalInvoice() | currency:'AUD':'symbol':'1.2-2' }}</div>
            <div class="metric-label">Total Invoice</div>
            <div class="metric-subtitle">{{ getMonthYearDisplay() }}</div>
          </mat-card-content>
        </mat-card>
      </div>
    </div>

    <!-- Impressions cache coverage: billing reads the cache, so missing days mean under-counted figures -->
    <div class="cache-banner in-progress" *ngIf="isMonthInProgress() && billingSummaries.length > 0">
      <mat-icon>schedule</mat-icon>
      <span>{{ getMonthYearDisplay() }} is still in progress: figures are month-to-date, not an invoice.</span>
    </div>
    <div class="cache-banner complete" *ngIf="isMonthFullyCached()">
      <mat-icon>check_circle</mat-icon>
      <span *ngIf="!isMonthInProgress()">Cache complete: all {{ cachePlan?.companies?.length }} companies have {{ getDaysInMonth() }}/{{ getDaysInMonth() }} days cached for {{ getMonthYearDisplay() }}.</span>
      <span *ngIf="isMonthInProgress()">Cache healthy: all {{ cachePlan?.companies?.length }} companies are cached through day {{ getExpectedCachedDays() }} of {{ getDaysInMonth() }}.</span>
    </div>
    <div class="cache-banner incomplete" *ngIf="cachePlan && !hasNoDaysToCheck() && !isMonthFullyCached()">
      <mat-icon>warning</mat-icon>
      <span>
        Cache incomplete: {{ getIncompleteCacheCompanies().length }} of {{ cachePlan.companies?.length || 0 }} companies
        <a class="cache-detail"
           [matTooltip]="getIncompleteCacheTooltip()"
           matTooltipClass="cache-tooltip">are missing days</a>
        <ng-container *ngIf="isMonthInProgress()">up to day {{ getExpectedCachedDays() }}</ng-container>
        for {{ getMonthYearDisplay() }}. Figures may be under-counted until the warmer fills them.
      </span>
    </div>
    <div class="cache-banner unknown" *ngIf="hasNoDaysToCheck()">
      <mat-icon>info</mat-icon>
      <span>No days of {{ getMonthYearDisplay() }} have been cached yet: the warmer caches each day at 00:30 UTC the next day.</span>
    </div>
    <div class="cache-banner unknown" *ngIf="cacheLoadFailed">
      <mat-icon>help_outline</mat-icon>
      <span>Couldn't check cache coverage for {{ getMonthYearDisplay() }}.</span>
    </div>

    <mat-card class="billing-table-card">
      <mat-card-header>
        <mat-card-title>
          {{ getMonthYearDisplay() }} Billing Summary
        </mat-card-title>
        <div class="card-actions">
          <button mat-stroked-button 
                  color="accent" 
                  (click)="exportToCsv()"
                  [disabled]="loading || billingSummaries.length === 0">
            <mat-icon>download</mat-icon>
            Export CSV
          </button>
        </div>
      </mat-card-header>
      
      <mat-card-content>
        <div class="table-container" [class.loading]="loading">
          <mat-progress-bar *ngIf="loading" mode="indeterminate" color="accent"></mat-progress-bar>
          
          <table mat-table [dataSource]="billingSummaries" class="billing-table">
            <!-- Company Column -->
            <ng-container matColumnDef="company">
              <th mat-header-cell *matHeaderCellDef>Company</th>
              <td mat-cell *matCellDef="let row">
                <div class="company-cell">
                  <strong>{{ row.companyName }}</strong>
                  <small class="company-id">
                    <a class="company-link"
                       (click)="openCompanyGraph(row.companyId)"
                       matTooltip="View company graph">{{ row.companyId }}</a>
                  </small>
                </div>
              </td>
            </ng-container>

            <!-- Licence Fee Column -->
            <ng-container matColumnDef="licenceFee">
              <th mat-header-cell *matHeaderCellDef>Licence Fee</th>
              <td mat-cell *matCellDef="let row">
                <span *ngIf="row.licenceFee">{{ row.licenceFee | currency:'AUD':'symbol':'1.0-0' }}</span>
              </td>
            </ng-container>

            <!-- Billable Impressions Column -->
            <ng-container matColumnDef="billable">
              <th mat-header-cell *matHeaderCellDef>Billable Impressions</th>
              <td mat-cell *matCellDef="let row">
                <span class="metric-value billable">{{ row.billableCount | number }}</span>
              </td>
            </ng-container>

            <!-- Rate Column -->
            <ng-container matColumnDef="rate">
              <th mat-header-cell *matHeaderCellDef>Rate</th>
              <td mat-cell *matCellDef="let row">
                <span *ngIf="row.rate">{{ row.rate | number:'1.2-2' }}</span>
              </td>
            </ng-container>

            <!-- Invoiced Impressions Value Column -->
            <ng-container matColumnDef="invoicedImpressionsValue">
              <th mat-header-cell *matHeaderCellDef>Invoiced Impressions</th>
              <td mat-cell *matCellDef="let row">
                <span *ngIf="row.invoicedImpressionsValue">{{ row.invoicedImpressionsValue | currency:'AUD':'symbol':'1.2-2' }}</span>
              </td>
            </ng-container>

            <!-- Total Invoice Column -->
            <ng-container matColumnDef="totalInvoice">
              <th mat-header-cell *matHeaderCellDef>Total Invoice</th>
              <td mat-cell *matCellDef="let row">
                <span class="metric-value invoice" *ngIf="row.totalInvoice">{{ row.totalInvoice | currency:'AUD':'symbol':'1.2-2' }}</span>
              </td>
            </ng-container>

            <!-- Previous Month Billable Column -->
            <ng-container matColumnDef="previousMonthBillable">
              <th mat-header-cell *matHeaderCellDef>Prev. Month Billable</th>
              <td mat-cell *matCellDef="let row">
                <span *ngIf="row.previousMonthBillableImpressions != null">{{ row.previousMonthBillableImpressions | number }}</span>
              </td>
            </ng-container>

            <!-- Variance Column -->
            <ng-container matColumnDef="variance">
              <th mat-header-cell *matHeaderCellDef>Variance</th>
              <td mat-cell *matCellDef="let row">
                <div class="variance-cell" *ngIf="getVariance(row) != null">
                  <span [class.positive]="getVariance(row) > 0"
                        [class.negative]="getVariance(row) < 0">
                    {{ getVariance(row) | number }}
                  </span>
                  <small class="variance-pct"
                         *ngIf="row.monthOverMonthChangePercent != null"
                         [class.positive]="row.monthOverMonthChangePercent > 0"
                         [class.negative]="row.monthOverMonthChangePercent < 0">
                    {{ row.monthOverMonthChangePercent > 0 ? '+' : '' }}{{ row.monthOverMonthChangePercent | number:'1.1-1' }}%
                  </small>
                </div>
              </td>
            </ng-container>

            <!-- Days Cached Column -->
            <ng-container matColumnDef="daysCached">
              <th mat-header-cell *matHeaderCellDef>Days Cached</th>
              <td mat-cell *matCellDef="let row">
                <ng-container *ngIf="cachePlan">
                  <span *ngIf="getCacheCoverage(row) as coverage"
                        class="days-cached"
                        [class.complete]="isCompanyFullyCached(row)"
                        [class.incomplete]="!isCompanyFullyCached(row)">
                    {{ coverage.daysCached || 0 }}/{{ getExpectedCachedDays() }}
                  </span>
                  <span *ngIf="!getCacheCoverage(row)"
                        class="days-cached not-warmed"
                        matTooltip="Not in the cache warmer's billable company list">&mdash;</span>
                </ng-container>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
          </table>

          <div *ngIf="!loading && billingSummaries.length === 0" class="no-data">
            <mat-icon>info</mat-icon>
            <p>No billing data available for {{ getMonthYearDisplay() }}</p>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

  </div>
</div>